/**
 * CollectionControlPanel - Interactive controls for PhotoCollection2 properties
 *
 * Provides UI controls for real-time adjustment of collection display properties.
 * Controls are added incrementally across implementation checkpoints.
 *
 * @module CollectionControlPanel
 */

import React from 'react';
import { CollectionControlPanelProps } from './CollectionControlPanel.types';
import { cn } from '@/lib/utils';
import { templates } from '@/components/PhotoItem2/templates';

/**
 * Convert kebab-case template name to human-readable label
 * @example "collection-view-exif" -> "Collection View Exif"
 */
function formatTemplateName(templateName: string): string {
  return templateName
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * CollectionControlPanel component
 *
 * Checkpoint 5: Layout, template, selection mode selectors, and zoom slider
 */
export const CollectionControlPanel: React.FC<CollectionControlPanelProps> = ({
  layout,
  itemTemplate,
  selectionMode,
  zoom,
  detailLocation,
  onLayoutChange,
  onItemTemplateChange,
  onSelectionModeChange,
  onZoomChange,
  onDetailLocationChange,
  className = '',
}) => {
  const handleLayoutChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    if (onLayoutChange) {
      onLayoutChange(event.target.value as any);
    }
  };

  const handleTemplateChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    if (onItemTemplateChange) {
      onItemTemplateChange(event.target.value as any);
    }
  };

  const handleSelectionModeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    if (onSelectionModeChange) {
      onSelectionModeChange(event.target.value as any);
    }
  };

  const handleZoomChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (onZoomChange) {
      onZoomChange(parseFloat(event.target.value));
    }
  };

  const handleDetailLocationChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    if (onDetailLocationChange) {
      onDetailLocationChange(event.target.value as any);
    }
  };

  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-6 p-4 bg-surface-1 rounded-lg border border-border',
        className
      )}
    >
      {/* Layout Selector */}
      {layout !== undefined && onLayoutChange && (
        <div className="flex items-center gap-3">
          <label htmlFor="layout-select" className="text-sm font-medium text-foreground">
            Layout:
          </label>
          <select
            id="layout-select"
            value={layout}
            onChange={handleLayoutChange}
            className="px-3 py-2 text-sm border border-border rounded-md bg-surface-2 text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="grid">Grid</option>
            <option value="magazine">Magazine</option>
            <option value="horizontal">Horizontal</option>
            <option value="vertical">Vertical</option>
          </select>
        </div>
      )}

      {/* Item Template Selector */}
      {itemTemplate !== undefined && onItemTemplateChange && (
        <div className="flex items-center gap-3">
          <label htmlFor="item-template-select" className="text-sm font-medium text-foreground">
            Item Template:
          </label>
          <select
            id="item-template-select"
            value={itemTemplate}
            onChange={handleTemplateChange}
            className="px-3 py-2 text-sm border border-border rounded-md bg-surface-2 text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          >
            {Object.keys(templates).map((templateName) => (
              <option key={templateName} value={templateName}>
                {formatTemplateName(templateName)}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Selection Mode Selector */}
      {selectionMode !== undefined && onSelectionModeChange && (
        <div className="flex items-center gap-3">
          <label htmlFor="selection-mode-select" className="text-sm font-medium text-foreground">
            Selection Mode:
          </label>
          <select
            id="selection-mode-select"
            value={selectionMode}
            onChange={handleSelectionModeChange}
            className="px-3 py-2 text-sm border border-border rounded-md bg-surface-2 text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="none">None</option>
            <option value="single">Single (Optional)</option>
            <option value="always-one">Always One</option>
            <option value="multiple">Multiple</option>
          </select>
        </div>
      )}

      {/* Zoom Slider - Hidden for vertical layout (zoom not meaningful for full-width display) */}
      {zoom !== undefined && onZoomChange && layout !== 'vertical' && (
        <div className="flex items-center gap-3">
          <label htmlFor="zoom-slider" className="text-sm font-medium text-foreground">
            Zoom:
          </label>
          <input
            id="zoom-slider"
            type="range"
            min="0.25"
            max="5.0"
            step="0.25"
            value={zoom}
            onChange={handleZoomChange}
            className="w-32 h-2 bg-surface-3 rounded-lg appearance-none cursor-pointer accent-primary"
          />
          <span className="text-sm font-mono text-foreground min-w-[3.5rem]">
            {Math.round(zoom * 100)}%
          </span>
        </div>
      )}

      {/* Detail Location Selector */}
      {detailLocation !== undefined && onDetailLocationChange && (
        <div className="flex items-center gap-3">
          <label htmlFor="detail-location-select" className="text-sm font-medium text-foreground">
            Detail View:
          </label>
          <select
            id="detail-location-select"
            value={detailLocation}
            onChange={handleDetailLocationChange}
            className="px-3 py-2 text-sm border border-border rounded-md bg-surface-2 text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="none">None (Collection Only)</option>
            <option value="top">Top</option>
            <option value="bottom">Bottom</option>
            <option value="left">Left</option>
            <option value="right">Right</option>
          </select>
        </div>
      )}
    </div>
  );
};

CollectionControlPanel.displayName = 'CollectionControlPanel';
